import { motion } from 'framer-motion';
import { 
  MapPin, Phone, Clock, MessageCircle, Star,
  Sparkles, Building2, Home, Factory, ChevronRight, Mail,
  CheckCircle2
} from 'lucide-react';

const DemoCleanForce = () => {
  const business = {
    name: "Clean-Force BV",
    tagline: "Professionele schoonmaakdiensten voor bedrijf & particulier",
    website: "www.clean-force.eu",
    hero: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&q=72&auto=format",
    rating: 4.9,
    reviews: 142,
    address: "Industrielaan 25, 6229 NS Maastricht",
    phone: "+31 43 355 0000",
    whatsapp: "31433550000",
    email: "info@clean-force.eu",
    hours: "Ma-Vr: 07:00-19:00 | Za: 08:00-14:00",
  };

  const services = [
    { icon: Building2, name: 'Kantoren', desc: 'Dagelijkse schoonmaak' },
    { icon: Home, name: 'Particulier', desc: 'Huishoudelijke hulp' },
    { icon: Factory, name: 'Industrie', desc: 'Fabrieken & hallen' },
    { icon: Sparkles, name: 'Specialistisch', desc: 'Glazenwassen & meer' },
  ];

  const priceList = [
    { service: 'Kantoorschoonmaak', unit: 'vanaf', price: '18/uur' },
    { service: 'Huishoudelijke hulp', unit: 'vanaf', price: '16/uur' },
    { service: 'Glazenwassen', unit: 'vanaf', price: '25/uur' },
    { service: 'Eindschoonmaak', unit: 'vanaf', price: '150' },
    { service: 'Bouwschoonmaak', unit: 'offerte', price: 'op maat' },
    { service: 'Periodiek contract', unit: 'korting', price: '-15%' },
  ];

  const uspPoints = [
    'Gecertificeerd & verzekerd',
    'Eigen vaste teams',
    'Milieuvriendelijke producten',
    'Flexibele planning',
  ];

  const whatsappMessage = encodeURIComponent("Hallo Clean-Force! Ik wil graag een vrijblijvende offerte aanvragen.");

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Hero */}
      <div className="relative h-56 overflow-hidden">
        <img 
          src={business.hero} 
          alt={`${business.name} - Professioneel schoonmaakbedrijf in België`}
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-50 via-transparent to-transparent" />
        
        {/* Logo */}
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -bottom-10 left-1/2 -translate-x-1/2"
        >
          <div className="w-20 h-20 rounded-2xl border-4 border-white overflow-hidden shadow-xl bg-emerald-600 flex items-center justify-center">
            <Sparkles className="text-white" size={36} />
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="pt-14 pb-8 px-6 max-w-lg mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{business.name}</h1>
          <p className="text-gray-500 text-sm mb-2">{business.tagline}</p>
          <a href={`https://${business.website}`} target="_blank" rel="noopener noreferrer" className="text-emerald-600 text-sm font-medium">
            {business.website}
          </a>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={14} 
                  className={i < Math.floor(business.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} 
                />
              ))}
            </div>
            <span className="text-gray-900 font-medium">{business.rating}</span>
            <span className="text-gray-400">({business.reviews} reviews)</span>
          </div>
        </motion.div>

        {/* WhatsApp CTA */}
        <motion.a
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          href={`https://wa.me/${business.whatsapp}?text=${whatsappMessage}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-3 bg-[#25D366] text-white py-4 px-6 rounded-2xl font-semibold mb-4 shadow-lg shadow-green-200 hover:shadow-xl transition-shadow"
          data-testid="whatsapp-btn"
        >
          <MessageCircle size={24} fill="white" />
          Vraag gratis offerte aan
        </motion.a>

        {/* Email alternative */}
        <motion.a
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          href={`mailto:${business.email}`}
          className="flex items-center justify-center gap-2 text-emerald-600 mb-6 text-sm"
        >
          <Mail size={16} />
          Of mail naar {business.email}
        </motion.a>

        {/* USP Points */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-emerald-50 rounded-2xl p-4 mb-6"
        >
          <div className="grid grid-cols-2 gap-2">
            {uspPoints.map((point, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
                {point}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 gap-3 mb-6"
        >
          {services.map((service, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <service.icon className="text-emerald-600 mb-2" size={24} />
              <h3 className="font-semibold text-gray-900 text-sm">{service.name}</h3>
              <p className="text-gray-500 text-xs">{service.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* Price List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-6"
        >
          <h2 className="text-lg font-bold text-gray-900 mb-3">Indicatie tarieven</h2>
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
            {priceList.map((item, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-4 ${
                  index !== priceList.length - 1 ? 'border-b border-gray-100' : ''
                }`}
              >
                <div>
                  <p className="font-medium text-gray-900">{item.service}</p>
                  <p className="text-gray-400 text-sm">{item.unit}</p>
                </div>
                <span className="font-bold text-emerald-600 text-lg">€{item.price}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Info Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-3 mb-6"
        >
          <div className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm border border-gray-100">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Clock className="text-emerald-600" size={20} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Bereikbaar</p>
              <p className="text-gray-900 font-medium">{business.hours}</p>
            </div>
          </div>
          
          <a 
            href={`https://maps.google.com/?q=${encodeURIComponent(business.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm border border-gray-100"
          >
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <MapPin className="text-emerald-600" size={20} />
            </div>
            <div className="flex-grow">
              <p className="text-gray-400 text-sm">Kantoor</p>
              <p className="text-gray-900 font-medium">{business.address}</p>
            </div>
            <ChevronRight className="text-gray-400" size={20} />
          </a>

          <a 
            href={`tel:${business.phone}`}
            className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm border border-gray-100"
          >
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Phone className="text-emerald-600" size={20} />
            </div>
            <div className="flex-grow">
              <p className="text-gray-400 text-sm">Telefoon</p>
              <p className="text-gray-900 font-medium">{business.phone}</p>
            </div>
            <ChevronRight className="text-gray-400" size={20} />
          </a>
        </motion.div>

        {/* Powered by */}
        <div className="text-center">
          <a href="https://netblitz.eu" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-xs hover:text-emerald-600 transition-colors">
            Powered by <span className="font-medium">Netblitz</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default DemoCleanForce;
